import { Component, ReactElement } from 'react';
import { ChevronDown, ChevronUp, XSquare } from 'react-bootstrap-icons';

import * as globals from './globals';
import * as Helpers from './helpers';
import { Filter, Filters, InputFilter, ListFilter, Movie, RawMovie, State, UrlFilters } from './types';
import CheckboxFilterComponent from './components/CheckboxFilterComponent';
import MovieComponent from './components/MovieComponent';
import NoDataFoundModalComponent from './components/NoDataFoundModalComponent';

import './App.scss';

export default class App extends Component<unknown, State> {
  filterExpandedStyle = { transform: 'translateY(-100%)' };

  filterCollapsedStyle = { transform: 'translateY(-62px)' };

  localStorageValues = {
    filterExpanded: 'expanded',
    filterCollapsed: 'collapsed',
  };

  constructor(props: never) {
    super(props);

    const inputFiltersInitialStateFn = (): null => null;
    const inputFilters: InputFilter[] = [
      { key: 'fullText', type: 'search', label: 'Volltext', value: null, compareValue: null, initialStateFn: inputFiltersInitialStateFn },
      { key: 'title', type: 'search', label: 'Filmtitel', value: null, compareValue: null, initialStateFn: inputFiltersInitialStateFn },
      { key: 'timeFrom', type: 'time', label: '≥ Uhrzeit', value: null, compareValue: null, initialStateFn: inputFiltersInitialStateFn },
      { key: 'timeTo', type: 'time', label: '≤ Uhrzeit', value: null, compareValue: null, initialStateFn: inputFiltersInitialStateFn },
      { key: 'durationFrom', type: 'number', label: '≥ Dauer', value: null, compareValue: null, initialStateFn: inputFiltersInitialStateFn },
      { key: 'durationTo', type: 'number', label: '≤ Dauer', value: null, compareValue: null, initialStateFn: inputFiltersInitialStateFn },
      { key: 'yearFrom', type: 'number', label: '≥ Jahr', value: null, compareValue: null, initialStateFn: inputFiltersInitialStateFn },
      { key: 'yearTo', type: 'number', label: '≤ Jahr', value: null, compareValue: null, initialStateFn: inputFiltersInitialStateFn },
    ];

    const listFiltersInitialStateFn = (filter: Filter): string[] => (filter as ListFilter).allValues.slice();
    const listFilters: ListFilter[] = [
      { key: 'days', label: 'Tage', allValues: [], currentValues: [], initialStateFn: listFiltersInitialStateFn },
      { key: 'cinemas', label: 'Kinos', allValues: [], currentValues: [], initialStateFn: listFiltersInitialStateFn },
      { key: 'categories', label: 'Kategorien', allValues: [], currentValues: [], initialStateFn: listFiltersInitialStateFn },
      { key: 'sections', label: 'Rubriken', allValues: [], currentValues: [], initialStateFn: listFiltersInitialStateFn },
    ];

    let filterStyle = this.filterCollapsedStyle;
    if (localStorage.diagonale_filterVisibility != null && [this.localStorageValues.filterExpanded, this.localStorageValues.filterCollapsed].includes(localStorage.diagonale_filterVisibility)) {
      filterStyle = (localStorage.diagonale_filterVisibility === this.localStorageValues.filterExpanded) ? this.filterExpandedStyle : this.filterCollapsedStyle;
    }

    this.state = {
      noDataFoundModalShow: false,
      loadingFinished: false,
      filterStyle,
      inputFilters,
      listFilters,
    };
  }

  componentDidMount(): void {
    this.loadMovies();
  }

  setFilterStyle(): void {
    this.setState((state: State) => {
      const filterStyle = (state.filterStyle === this.filterExpandedStyle) ? this.filterCollapsedStyle : this.filterExpandedStyle;
      localStorage.diagonale_filterVisibility = (filterStyle === this.filterExpandedStyle) ? this.localStorageValues.filterExpanded : this.localStorageValues.filterCollapsed;
      return { filterStyle };
    });
  }

  initFilters = (): void => {
    this.setState((state: State) => {
      const { inputFilters, listFilters } = state;
      for (const inputFilter of inputFilters) {
        Helpers.setInputFilterValue(inputFilter, inputFilter.initialStateFn());
      }
      for (const listFilter of listFilters) {
        Helpers.setListFilterValue(listFilter, listFilter.initialStateFn(listFilter));
      }

      let { year } = state;

      if (window.location.hash) {
        try {
          const urlState = Helpers.getUrlState();
          if (urlState.filters != null) {
            for (const [key, value] of Object.entries(urlState.filters)) {
              const inputFilter = inputFilters.find((f) => f.key === key);
              if (inputFilter != null) {
                Helpers.setInputFilterValue(inputFilter, value as string);
                continue;
              }
              const listFilter = listFilters.find((f) => f.key === key);
              if (listFilter != null) {
                Helpers.setListFilterValue(listFilter, value as string[]);
              }
            }
          }
          if (urlState.year != null) {
            year = urlState.year;
          }
        } catch (e) {
          console.error(e);
        }
      }

      return {
        inputFilters: inputFilters.slice(),
        listFilters: listFilters.slice(),
        year,
      };
    }, this.applyFilter);
  };

  loadMovies = async (): Promise<void> => {
    let year: number;
    let rawMovies: RawMovie[] | null;
    const urlState = Helpers.getUrlState();

    if (urlState.year != null) {
      year = urlState.year;
      rawMovies = await Helpers.fetchMoviesFromFile(urlState.year);
      if (rawMovies == null) {
        window.location.href = process.env.PUBLIC_URL as string;
        return;
      }
    } else {
      year = globals.CURRENT_YEAR;
      rawMovies = null;
      while (year >= globals.FIRST_YEAR) {
        rawMovies = await Helpers.fetchMoviesFromFile(year);
        if (rawMovies != null) {
          break;
        }
        if (year === globals.CURRENT_YEAR && rawMovies == null) {
          this.setState({ noDataFoundModalShow: true, noDataFoundModalYear: year });
        }
        year -= 1;
      }
      if (rawMovies == null) {
        this.setState({ loadingFinished: true });
        return;
      }
    }

    const movies = Helpers.preProcessMovies(rawMovies, year);

    const { listFilters } = this.state;

    for (const listFilter of listFilters) {
      switch (listFilter.key) {
        case 'days':
          listFilter.allValues = Array.from(new Set(movies.map((movie) => movie.dates).flat().map((date) => date.datetime.substring(0, 10)))).filter((day) => day != null).sort(Helpers.sortAlphabetically).map((datetime) => new Date(datetime).toLocaleDateString(globals.LOCALE, globals.LOCALE_DATE));
          break;
        case 'cinemas':
          listFilter.allValues = Array.from(new Set(movies.map((movie) => movie.dates).flat().map((date) => date.venue))).filter((cinema) => cinema != null).sort(Helpers.sortAlphabetically);
          break;
        case 'categories':
          listFilter.allValues = (Array.from(new Set(movies.map((movie) => movie.category))).filter((category) => category != null) as string[]).sort(Helpers.sortAlphabetically);
          break;
        case 'sections':
          listFilter.allValues = (Array.from(new Set(movies.map((movie) => movie.section))).filter((section) => section != null) as string[]).sort(Helpers.sortAlphabetically);
          break;
        default:
          throw Error(`List filter "${listFilter.key}" does not have any values assigned.`);
      }
    }

    this.setState({ movies, year, listFilters }, this.initFilters);
  };

  writeFilterStateToUrl = (): void => {
    const { inputFilters, listFilters, year } = this.state;

    const urlFilters: UrlFilters = {};
    const activeFilters: Filter[] = [];

    for (const inputFilter of inputFilters) {
      if (inputFilter.value != null) {
        urlFilters[inputFilter.key] = inputFilter.value;
        activeFilters.push(inputFilter);
      }
    }
    for (const listFilter of listFilters) {
      if (listFilter.currentValues.length !== listFilter.allValues.length) {
        urlFilters[listFilter.key] = listFilter.currentValues;
        activeFilters.push(listFilter);
      }
    }

    const hasChangedFilters = activeFilters.length > 0;
    if (window.location.hash !== '' || hasChangedFilters) {
      window.history.replaceState(undefined, '', `#${hasChangedFilters ? encodeURIComponent(JSON.stringify({ filters: urlFilters, year })) : ''}`);
    }
    this.setState({ activeFilters, loadingFinished: true });
  };

  applyFilter = (): void => {
    this.setState((state: State) => {
      const { movies } = state;
      if (movies == null) {
        throw Error('No movies set.');
      }

      const { inputFilters, listFilters } = state;
      const filters: Filters = {};
      for (const inputFilter of inputFilters) {
        filters[inputFilter.key] = inputFilter;
      }
      for (const listFilter of listFilters) {
        filters[listFilter.key] = listFilter;
      }

      const filteredMovies = movies.filter((movie) => {
        const passesTextFilters =
          (filters.title?.compareValue == null || (filters.title.compareValue as RegExp).test(movie.title)) &&
          (filters.fullText?.compareValue == null || (filters.fullText.compareValue as RegExp).test(movie.fullText));

        const passesDatesFilters = movie.dates.some((date) => (
          (filters.timeFrom?.compareValue == null || date.minutesOfDay >= (filters.timeFrom.compareValue as number)) &&
          (filters.timeTo?.compareValue == null || date.minutesOfDay <= (filters.timeTo.compareValue as number)) &&
          ((filters.cinemas?.allValues.length ?? 0) <= 0 || filters.cinemas?.currentValues.includes(date.venue)) &&
          ((filters.days?.allValues.length ?? 0) <= 0 || filters.days?.currentValues.includes(date.dayString))
        ));

        const passesCheckboxFilters =
          (filters.durationFrom?.compareValue == null || (movie.details.durationInMinutes != null && movie.details.durationInMinutes >= (filters.durationFrom.compareValue as number))) &&
          (filters.durationTo?.compareValue == null || (movie.details.durationInMinutes != null && movie.details.durationInMinutes <= (filters.durationTo.compareValue as number))) &&
          (filters.yearFrom?.compareValue == null || (movie.details.year != null && movie.details.year >= (filters.yearFrom.compareValue as number))) &&
          (filters.yearTo?.compareValue == null || (movie.details.year != null && movie.details.year <= (filters.yearTo.compareValue as number))) &&
          (movie.category == null || (filters.categories?.allValues.length ?? 0) <= 0 || filters.categories?.currentValues.includes(movie.category)) &&
          (movie.section == null || (filters.sections?.allValues.length ?? 0) <= 0 || filters.sections?.currentValues.includes(movie.section));

        return passesTextFilters && passesDatesFilters && passesCheckboxFilters;
      });

      return { filteredMovies };
    }, this.writeFilterStateToUrl);
  };

  setFilterState = (key: string, value: string | string[] | null): void => {
    this.setState((state: State) => {
      const { inputFilters, listFilters } = state;
      const inputFilter = inputFilters.find((f) => f.key === key);
      if (inputFilter != null) {
        Helpers.setInputFilterValue(inputFilter, value as string | null);
      }
      const listFilter = listFilters.find((f) => f.key === key);
      if (listFilter != null) {
        Helpers.setListFilterValue(listFilter, value as string[]);
      }
      return { inputFilters, listFilters };
    }, this.applyFilter);
  };

  setInputFilterState = (event: { target: HTMLInputElement }): void => {
    this.setFilterState(event.target.id, (event.target.value === '') ? null : event.target.value);
  };

  updateListFilterState = (filterKey: string, updateValue: string, currentValues: string[]): void => {
    const updateValueIdx = currentValues.findIndex((currentValue) => currentValue === updateValue);
    const newValues = currentValues.slice();
    if (updateValueIdx >= 0) {
      newValues.splice(updateValueIdx, 1);
    } else {
      newValues.push(updateValue);
    }
    this.setFilterState(filterKey, newValues);
  };

  render(): ReactElement {
    const { loadingFinished, filteredMovies, activeFilters, year, movies, filterStyle, inputFilters, listFilters, noDataFoundModalYear, noDataFoundModalShow } = this.state;
    return (
      <div>
        {loadingFinished && filteredMovies != null && activeFilters != null
        // Main container
        && (
        <div className="container-fluid pt-3 d-flex flex-column align-items-center">
          <a href="https://buymeacoffee.com/geymayer" target="_blank" rel="noreferrer" className="position-fixed top-0 end-0 p-1 rounded fs-1 lh-1" style={{ zIndex: 10 }}>☕</a>
          <div className="text-center w-100 mb-4">
            <h1>Diagonale {year}</h1>
            <div><b>Inoffizieller Programmfilter</b></div>
            {listFilters.length > 0 && <div>{listFilters.find((lf) => lf.key === 'days')?.allValues[0]} &ndash; {listFilters.find((lf) => lf.key === 'days')?.allValues.at(-1)}</div>}
            {/* <div className="mt-1"><b><a href="https://buymeacoffee.com/geymayer" target="_blank" rel="noreferrer">Daniel einen ☕ ausgeben</a></b></div> */}
          </div>
          {/* Movies list */}
          {movies != null && (
          <div id="movies" className="w-100">
            {filteredMovies.length > 0 && (
            <div className="row row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-xl-4 row-cols-xxl-5 g-3">
              {filteredMovies.sort(Helpers.sortAlphabeticallyByTitle).map((movie: Movie) => (
                <MovieComponent key={movie.websiteUrl} movie={movie} />
              ))}
            </div>
            )}
            {filteredMovies.length <= 0 && (
            <div className="alert alert-danger text-center">
              Mit dem aktuellen Filter konnten keine Filme gefunden werden, vielleicht solltest du was daran ändern! <span role="img" aria-label="wink">😘</span>
            </div>
            )}
          </div>
          )}
          {/* Footer */}
          <footer className="text-center w-100">
            <hr />
            Erstellt von <a href="https://geymayer.com" target="_blank" rel="noopener noreferrer">Daniel Geymayer</a>
            <br />
            <a href="https://buymeacoffee.com/geymayer" target="_blank" rel="noopener noreferrer">Daniel einen ☕ ausgeben</a>
            <br />
            Daten von <a href="http://www.diagonale.at" target="_blank" rel="noopener noreferrer">diagonale.at</a>
          </footer>
          {/* Fixed bottom container */}
          {movies != null && (
          <div className="fixed-bottom-container container-lg px-0 position-fixed bottom-0 d-flex justify-content-center">
            {/* Filter toggle button */}
            <button type="button" className="filter-toggle btn btn-primary btn-sm position-absolute top-0 end-0 d-flex align-items-center gap-1 z-2" onClick={(): void => { this.setFilterStyle(); }}>
              {(filterStyle === this.filterExpandedStyle) ? <ChevronDown /> : <ChevronUp />}<b>Filter</b>{(filterStyle === this.filterExpandedStyle) ? <ChevronDown /> : <ChevronUp />}
            </button>
            {/* Filter drawer */}
            <div id="filters" className="filters row w-100 position-absolute top-0 py-3 d-flex justify-content-center gap-3 gap-md-0 overflow-y-auto bg-primary" style={filterStyle}>
              <div className="textFields col-12 col-md-6 d-flex flex-column gap-2">
                {inputFilters.filter((inputFilter) => ['fullText', 'title'].includes(inputFilter.key)).map((inputFilter) => (
                  <div key={inputFilter.key} className="row">
                    <label htmlFor={inputFilter.key} className="col-3 col-sm-2 col-form-label col-form-label-sm font-weight-bold">{inputFilter.label}</label>
                    <div className="col-9 col-sm-10">
                      <input type={inputFilter.type} id={inputFilter.key} className="form-control form-control-sm" placeholder="Suchen ..." value={inputFilter.value ?? ''} onChange={this.setInputFilterState} />
                    </div>
                  </div>
                ))}
                <div className="row">
                  <label htmlFor="timeFrom" className="col-3 col-sm-2 col-form-label col-form-label-sm">Uhrzeit</label>
                  <div className="col-9 col-sm-10">
                    <div className="input-group input-group-sm ">
                      {inputFilters.filter((inputFilter) => inputFilter.key === 'timeFrom').map((inputFilter) => (
                        <input type={inputFilter.type} key={inputFilter.key} id={inputFilter.key} className="form-control" value={inputFilter.value ?? ''} onChange={this.setInputFilterState} />
                      ))}
                      <div className="input-group-text">&ndash;</div>
                      {inputFilters.filter((inputFilter) => inputFilter.key === 'timeTo').map((inputFilter) => (
                        <input type={inputFilter.type} key={inputFilter.key} id={inputFilter.key} className="form-control" value={inputFilter.value ?? ''} onChange={this.setInputFilterState} />
                      ))}
                      <div className="input-group-text"><small>Uhr</small></div>
                    </div>
                  </div>
                </div>
                <div className="row">
                  <label htmlFor="durationFrom" className="col-3 col-sm-2 col-form-label col-form-label-sm">Dauer</label>
                  <div className="col-9 col-sm-10">
                    <div className="input-group input-group-sm ">
                      {inputFilters.filter((inputFilter) => inputFilter.key === 'durationFrom').map((inputFilter) => (
                        <input type={inputFilter.type} key={inputFilter.key} id={inputFilter.key} className="form-control" inputMode="numeric" min="0" value={inputFilter.value ?? ''} onChange={this.setInputFilterState} />
                      ))}
                      <div className="input-group-text">&ndash;</div>
                      {inputFilters.filter((inputFilter) => inputFilter.key === 'durationTo').map((inputFilter) => (
                        <input type={inputFilter.type} key={inputFilter.key} id={inputFilter.key} className="form-control" inputMode="numeric" min="0" value={inputFilter.value ?? ''} onChange={this.setInputFilterState} />
                      ))}
                      <div className="input-group-text"><small>Min.</small></div>
                    </div>
                  </div>
                </div>
                <div className="row">
                  <label htmlFor="yearFrom" className="col-3 col-sm-2 col-form-label col-form-label-sm">Jahr</label>
                  <div className="col-9 col-sm-10">
                    <div className="input-group input-group-sm ">
                      {inputFilters.filter((inputFilter) => inputFilter.key === 'yearFrom').map((inputFilter) => (
                        <input type={inputFilter.type} key={inputFilter.key} id={inputFilter.key} className="form-control" inputMode="numeric" min="1800" max={year} value={inputFilter.value ?? ''} onChange={this.setInputFilterState} />
                      ))}
                      <div className="input-group-text">&ndash;</div>
                      {inputFilters.filter((inputFilter) => inputFilter.key === 'yearTo').map((inputFilter) => (
                        <input type={inputFilter.type} key={inputFilter.key} id={inputFilter.key} className="form-control" inputMode="numeric" min="1800" max={year} value={inputFilter.value ?? ''} onChange={this.setInputFilterState} />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              <div className="accordion col-12 col-md-6" id="filtersAccordion">
                {listFilters.filter((listFilter) => listFilter.allValues.length > 0).map((listFilter) => (
                  <div key={listFilter.key} className="accordion-item">
                    <h2 className="accordion-header" id={`filtersAccordionHeading-${listFilter.key}`}>
                      <button className="accordion-button collapsed d-flex gap-1 px-3 py-2" type="button" data-bs-toggle="collapse" data-bs-target={`#filtersAccordion-${listFilter.key}`} aria-expanded="false" aria-controls={`filtersAccordion-${listFilter.key}`}>
                        <small>{listFilter.label}</small><small className="badge text-muted">{listFilter.currentValues.length} / {listFilter.allValues.length}</small>
                      </button>
                    </h2>
                    <div id={`filtersAccordion-${listFilter.key}`} className="accordion-collapse collapse" aria-labelledby={`filtersAccordionHeading-${listFilter.key}`} data-bs-parent="#filtersAccordion">
                      <div className="accordion-body px-3 py-2">
                        <CheckboxFilterComponent listFilter={listFilter} set={this.setFilterState} update={this.updateListFilterState} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {/* Filter bar */}
            <div className="filter-bar w-100 p-2 d-flex flex-wrap justify-content-center align-items-center gap-2 bg-primary text-white z-1">
              {/* Active filters */}
              {activeFilters.length > 0 && <small>aktive Filter:</small>}
              {activeFilters.map((filter: Filter) => (
                <button type="button" key={filter.key} className="btn btn-outline-light btn-sm d-flex align-items-center gap-1" onClick={(): void => { this.setFilterState(filter.key, filter.initialStateFn(filter)); }}>
                  {filter.label}<XSquare />
                </button>
              ))}
              {/* Filters found */}
              <small className="text-nowrap"><b>{filteredMovies.length <= 0 ? 'keine' : filteredMovies.length}</b> Film{filteredMovies.length !== 1 && 'e'} gefunden{filteredMovies.length === movies.length && ' (alle)'}</small>
            </div>
          </div>
          )}
        </div>
        )}
        {/* Loading screen */}
        {!loadingFinished && (
        <div className="justify-content-center align-items-center d-flex position-fixed top-0 start-0 w-100 h-100">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Laden ...</span>
          </div>
        </div>
        )}
        {/* Modal */}
        {noDataFoundModalYear != null && <NoDataFoundModalComponent show={noDataFoundModalShow ? 1 : 0} onHide={(): void => { this.setState({ noDataFoundModalShow: false }); }} year={noDataFoundModalYear} />}
      </div>
    );
  }
}
